/**
 * Persistent "recently visited from global search" list backed by
 * localStorage. Survives reloads and is shared across browser tabs via
 * the existing useLocalStorage cross-tab listener.
 */
import { useCallback } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';

export type RecentEntityKind = 'company' | 'person';

export interface RecentSearchEntity {
  kind: RecentEntityKind;
  id: string;
  name: string;
  /** Secondary line (e.g. `UK · 12345678` or `CEO · Acme Co`). */
  subtitle?: string;
  /** Company-only — for the CompanyLogo prop chain. */
  logoUrl?: string;
  domain?: string;
  /** Person-only — direct photo URL when available. */
  photoUrl?: string;
  visitedAt: number;
}

const STORAGE_KEY = 'gsearch:recent';
const MAX_RECENTS = 5;

export interface UseRecentSearchEntitiesResult {
  recents: RecentSearchEntity[];
  addRecent: (entity: Omit<RecentSearchEntity, 'visitedAt'>) => void;
  clearRecents: () => void;
}

export function useRecentSearchEntities(): UseRecentSearchEntitiesResult {
  const [recents, setRecents] = useLocalStorage<RecentSearchEntity[]>(
    STORAGE_KEY,
    [],
  );

  const addRecent = useCallback(
    (entity: Omit<RecentSearchEntity, 'visitedAt'>) => {
      setRecents((prev) => {
        const dedup = prev.filter(
          (r) => !(r.kind === entity.kind && r.id === entity.id),
        );
        return [{ ...entity, visitedAt: Date.now() }, ...dedup].slice(
          0,
          MAX_RECENTS,
        );
      });
    },
    [setRecents],
  );

  const clearRecents = useCallback(() => {
    setRecents([]);
  }, [setRecents]);

  return { recents, addRecent, clearRecents };
}
